import React, { useState } from "react";
import { gql, useQuery } from "@apollo/client";
import "./RsvpAnswersTab.css";

// Admin RSVP Answers App: every NATIVE RSVP (the dashboard's
// Yes/Maybe/No buttons, socialEventRsvps table) grouped by event —
// the only surface that shows the ❌ No answers (members only ever
// see Yes and Maybe). Read-only; members manage their own answers
// from the dashboard event cards. Distinct from RsvpsTab, which is
// the GroupMe-poll roster + wins counter tool.

const ADMIN_EVENT_RSVPS = gql`
  query AdminEventRsvps {
    adminEventRsvps {
      socialEventId
      title
      yesCount
      maybeCount
      noCount
      entries {
        userSub
        name
        email
        status
        createdAt
      }
    }
  }
`;

const STATUS_META = {
  yes: { emoji: "✅", label: "Yes" },
  maybe: { emoji: "🤔", label: "Maybe" },
  no: { emoji: "❌", label: "No" },
};

const formatWhen = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const RsvpAnswersTab = () => {
  const { data, loading, error, refetch } = useQuery(ADMIN_EVENT_RSVPS, {
    fetchPolicy: "network-only",
  });
  const [openId, setOpenId] = useState(null);

  const events = data?.adminEventRsvps || [];

  return (
    <div className="rsvp-answers">
      <div className="rsvp-answers__head">
        <div>
          <h2 className="rsvp-answers__title">RSVP Answers</h2>
          <p className="rsvp-answers__sub">
            Every native RSVP by event, including ❌ No answers (members
            only see Yes and Maybe). Tap an event to expand.
          </p>
        </div>
        <button
          type="button"
          className="rsvp-answers__refresh"
          onClick={() => refetch()}
          disabled={loading}
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {error && (
        <div className="rsvp-answers__error">
          Couldn't load RSVPs: {error.message}
        </div>
      )}
      {!loading && !error && events.length === 0 && (
        <div className="rsvp-answers__empty">No RSVPs yet.</div>
      )}

      {events.map((ev) => {
        const open = openId === ev.socialEventId;
        return (
          <div key={ev.socialEventId} className="rsvp-answers__event">
            <button
              type="button"
              className="rsvp-answers__event-head"
              onClick={() => setOpenId(open ? null : ev.socialEventId)}
            >
              <span className="rsvp-answers__event-title">
                {ev.title || "(untitled)"}
                <span className="rsvp-answers__event-id">
                  {ev.socialEventId}
                </span>
              </span>
              <span className="rsvp-answers__counts">
                ✅ {ev.yesCount} · 🤔 {ev.maybeCount} · ❌ {ev.noCount}
                <span className="rsvp-answers__chevron">
                  {open ? "▾" : "▸"}
                </span>
              </span>
            </button>
            {open && (
              <div className="rsvp-answers__table-wrap">
                <table className="rsvp-answers__table">
                  <thead>
                    <tr>
                      <th>Answer</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>When</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ev.entries.map((e) => {
                      const meta =
                        STATUS_META[e.status] || STATUS_META.yes;
                      return (
                        <tr
                          key={e.userSub || e.email || e.name}
                          className={`rsvp-answers__row--${e.status}`}
                        >
                          <td className="rsvp-answers__answer">
                            {meta.emoji} {meta.label}
                          </td>
                          <td>{e.name || "—"}</td>
                          <td className="rsvp-answers__email">
                            {e.email || "—"}
                          </td>
                          <td className="rsvp-answers__when">
                            {formatWhen(e.createdAt)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default RsvpAnswersTab;
